import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { getLoginUrl } from '../../../helpers/authConfig'
import { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { chart, chartDefaults, colors } from '../../../ui'
import { StatTile } from '../../reusable'
import { useFundableClients } from '../../reusable/fundableClientsStat/useFundableClients'
import type { ChartData, ChartOptions } from 'chart.js'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

interface LineProps {
    options: ChartOptions<'line'>
    data: ChartData<'line'>
}

interface statsWithCharts extends statsProps {
    chartData?: any
    dataGroup?: string
    remarketedGroup?: string
    handleDataModal?: (data: any) => void
    initialShowChart?: Array<string>
    // When set, the "Clients with Claims" tile is replaced with the introducer's
    // fundable-clients figure for everyone. Admins additionally see the detailed
    // Fundable Clients card at the top; both read from the same endpoint, so the
    // tile and the card always agree.
    fundableIntroducer?: string
    // Optional PCP Referral campaign cohort ('current' | 'historical') so the
    // swapped tile matches the cohort of this StatsView instance.
    fundableCohort?: string
    // Render only these stat groups (by title). Mutually exclusive with
    // excludeTitles. Used to split a single overview payload across visibility
    // groups (e.g. show "Claims Progress" only in the Sentinel-admin section).
    onlyTitles?: string[]
    // Render every stat group EXCEPT these titles.
    excludeTitles?: string[]
}

// Label of the backend stat tile that gets swapped for the fundable-clients
// number. Matches the key emitted by the *SystemOverview dashboard functions
// (e.g. utilities/dashboardFunctions/mfcSystemOverview.js).
const CLIENTS_WITH_CLAIMS_LABEL = 'Clients with Claims'

const formatFundable = (n?: number) =>
    typeof n === 'number' && !isNaN(n) ? new Intl.NumberFormat('en-GB').format(n) : '0'

export const StatsView = (props: statsWithCharts) => {
    const {
        stats,
        chartData,
        dataGroup,
        remarketedGroup,
        handleDataModal,
        initialShowChart,
        fundableIntroducer,
        fundableCohort,
        onlyTitles,
        excludeTitles,
    } = props
    const hasClientsWithClaimsTile = Array.isArray(stats)
        ? stats.some(
              (group: any) =>
                  Array.isArray(group?.values) &&
                  group.values.some((value: any) => Object.keys(value)[0] === CLIENTS_WITH_CLAIMS_LABEL)
          )
        : false
    // Replace the legacy "Clients with Claims" tile with the fundable figure for
    // everyone (this also triggers the heavy fundability fetch). Admins still get
    // the detailed card at the top, sourced from the same endpoint.
    const showFundableSwap = !!fundableIntroducer && hasClientsWithClaimsTile
    const { data: fundableData, loading: fundableLoading, error: fundableError } = useFundableClients(
        fundableIntroducer,
        showFundableSwap,
        fundableCohort
    )
    const [dataGroupState, setDataGroupState] = useState('')
    const [showStats, setShowStats] = useState(false)
    const [sigChartData, setSigChartData] = useState<ChartData<'line'>>()
    const [showChart, setShowChart] = useState<Array<string>>(initialShowChart || ['All Leads Progress'])

    useEffect(() => {
        if (!showStats && stats.length > 0) setShowStats(true)
    }, [stats])

    useEffect(() => {
        if (dataGroup && dataGroup != '') setDataGroupState(dataGroup)
    }, [dataGroup])

    useEffect(() => {
        if (chartData) {
            setSigChartData(chartData)
        }
    }, [chartData])

    const defaultChartOptions: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'nearest',
            intersect: true,
            axis: 'x',
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: 600,
                    },
                },
            },
            tooltip: {
                backgroundColor: chartDefaults.tooltip.backgroundColor,
                titleColor: chartDefaults.tooltip.titleColor,
                bodyColor: chartDefaults.tooltip.bodyColor,
                cornerRadius: chartDefaults.tooltip.cornerRadius,
                padding: chartDefaults.tooltip.padding,
                titleFont: {
                    size: 13,
                    weight: 600,
                },
                bodyFont: {
                    size: 13,
                },
                callbacks: {
                    title: function (context) {
                        const date = new Date(context[0].label)
                        return date.toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                        })
                    },
                    label: function (context) {
                        let label = context.dataset.label || ''
                        if (label) {
                            label += ': '
                        }
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat('en-US').format(context.parsed.y)
                        }
                        return label
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: chartDefaults.gridColor,
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    font: {
                        size: 11,
                    },
                    callback: function (value) {
                        const date = new Date(this.getLabelForValue(value as number))
                        return date.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                        })
                    },
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: chartDefaults.gridColor,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    callback: function (tickValue: number | string) {
                        if (typeof tickValue === 'number') {
                            return new Intl.NumberFormat('en-US').format(tickValue)
                        }
                        return tickValue
                    },
                },
            },
        },
        animation: {
            duration: 750,
            easing: 'easeInOutQuart',
        },
        onHover: (evt: any, activeEls) =>
            !activeEls.length
                ? (evt.chart.canvas.style.cursor = 'default')
                : (evt.chart.canvas.style.cursor = 'pointer'),
        events: ['click', 'mousemove'],
        onClick: async (e: any, elements: any) => {
            const element = elements[0]
            const chart = e.chart
            if (typeof element == 'undefined') return
            const dataSet = chart.data.datasets[element.datasetIndex]
            const resultsDate = chart.data.labels[element.index]
            const expectedResults = dataSet.data[element.index]
            const resultsGroup = dataSet.label
            const chartName = chart.titleBlock.options.text

            // Determine which data group to use based on the chart name
            let currentDataGroup = dataGroupState
            if (chartName === 'PCP Remarketing') {
                currentDataGroup = remarketedGroup || dataGroupState
            }

            // Generate query based on dataGroup
            let query
            if (currentDataGroup === 'adlIL') {
                // ADL-specific query names
                if (resultsGroup === 'Clients Created') {
                    query = 'adlClientsCreatedByDate'
                } else if (resultsGroup === 'Clients Signed') {
                    query = 'adlClientsSignedByDate'
                } else if (resultsGroup === 'Clients Completed') {
                    query = 'adlClientsCompletedByDate'
                } else {
                    query = `${serialiseText(chartName)}-${serialiseText(resultsGroup)}`
                }
            } else if (currentDataGroup === 'introducerAdl') {
                // Introducer ADL-specific query names
                if (resultsGroup === 'Introducer ADL Clients Created') {
                    query = 'introducerAdlClientsCreatedByDate'
                } else if (resultsGroup === 'Introducer ADL Clients Signed') {
                    query = 'introducerAdlClientsSignedByDate'
                } else if (resultsGroup === 'Introducer ADL Clients Completed') {
                    query = 'introducerAdlClientsCompletedByDate'
                } else {
                    query = `${serialiseText(chartName)}-${serialiseText(resultsGroup)}`
                }
            } else if (currentDataGroup === 'mrIL') {
                // MR IL-specific query names
                if (resultsGroup === 'Clients Created') {
                    query = 'mrClientsCreatedByDate'
                } else if (resultsGroup === 'Clients Signed') {
                    query = 'mrClientsSignedByDate'
                } else if (resultsGroup === 'Clients Completed') {
                    query = 'mrClientsCompletedByDate'
                } else {
                    query = `${serialiseText(chartName)}-${serialiseText(resultsGroup)}`
                }
            } else if (currentDataGroup === 'remarketedMr') {
                // Remarketed MR-specific query names
                if (resultsGroup === 'Remarketed MR Clients Created') {
                    query = 'remarketedMrClientsCreatedByDate'
                } else {
                    query = `${serialiseText(chartName)}-${serialiseText(resultsGroup)}`
                }
            } else if (currentDataGroup === 'remarketedAdl') {
                // Remarketed ADL query Names
                if (resultsGroup === 'Remarketed ADL Clients Created') {
                    query = 'remarketedAdlClientsCreatedByDate'
                    // } else if (resultsGroup === 'Remarketed ADL Clients Signed') {
                    //     query = 'remarketedAdlClientsSignedByDate'
                    // } else if (resultsGroup === 'Remarketed ADL Clients Completed') {
                    //     query = 'remarketedAdlClientsCompletedByDate'
                } else {
                    query = `${serialiseText(chartName)}-${serialiseText(resultsGroup)}`
                }
            } else {
                // Default query format for other data groups
                query = `${serialiseText(chartName)}-${serialiseText(resultsGroup)}`
            }

            const requested = await chartClickData({
                query,
                resultsDate,
                expectedResults,
                chartName,
                resultsGroup,
                currentDataGroup,
            })
        },
    }

    const chartClickData = async (dataRequest: any) => {
        Loading.circle('Loading...')
        try {
            const apiResponse = await api({
                url: '/get-chart-dataset',
                data: { dataGroup: dataRequest.currentDataGroup || dataGroupState, dataRequest },
            })
            const data = apiResponse.data
            console.log('Chart Click API Response:', data)
            let dataResults = data.requestResults.results

            if (dataResults && typeof handleDataModal != 'undefined') {
                const niceDate = new Date(dataRequest.resultsDate).toDateString()
                const t = `${dataRequest.chartName} - ${dataRequest.resultsGroup} (${dataResults.length})`
                handleDataModal({
                    title: t,
                    date: niceDate,
                    dataResults,
                })
            }

            Loading.remove()
        } catch (error: any) {
            console.error('Error fetching chart data:', error)
            Loading.remove()
            if (error?.response?.status == 401) {
                Report.failure('Session Timed Out', 'Please log in again to continue.', 'Okay', () => {
                    window.location.href = getLoginUrl()
                })
            } else {
                Report.failure('Error', 'Failed to load chart data. Please try again.', 'Okay')
            }
        }
    }

    const serialiseText = (t: string) => {
        return t.replaceAll(' ', '_').toLowerCase()
    }

    const displayChart = (title: any) => {
        if (chartData && chartData[title] && showChart.some((x) => x == title)) {
            const chartDataWithDefaults = {
                ...chartData[title],
                datasets: chartData[title].datasets.map((dataset: any, i: number) => ({
                    ...dataset,
                    borderColor: chart.series[i % chart.series.length],
                    backgroundColor: chart.series[i % chart.series.length],
                    borderWidth: 2,
                    tension: 0.4,
                    fill: false,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    pointBackgroundColor: chart.series[i % chart.series.length],
                    pointBorderColor: colors.surface,
                    pointBorderWidth: 2,
                })),
            }

            return (
                <div className={styles.chartContainer}>
                    <Line
                        options={{
                            ...defaultChartOptions,
                            plugins: {
                                ...defaultChartOptions.plugins,
                                title: {
                                    display: false,
                                    text: title,
                                },
                            },
                        }}
                        data={chartDataWithDefaults}
                    />
                </div>
            )
        }
        return null
    }

    const toggleChart = (title: string) => {
        const tempShowChart = [...showChart]
        if (title) {
            if (tempShowChart.some((x) => x == title)) {
                setShowChart([...showChart.filter((x) => x != title)])
            } else {
                tempShowChart.push(title)
                setShowChart(tempShowChart)
            }
        }
    }

    const displayStats = () => {
        const visibleStats = Array.isArray(stats)
            ? stats.filter((elem: any) => {
                  if (onlyTitles) return onlyTitles.includes(elem?.title)
                  if (excludeTitles) return !excludeTitles.includes(elem?.title)
                  return true
              })
            : stats
        return visibleStats.map((elem: any, i: number) => {
            return (
                <div key={i}>
                    <div className={styles.statGroup}>
                        <h3 className={styles.statGroupTitle}>
                            {elem.title}
                            {chartData && chartData[elem.title] ? (
                                <button
                                    onClick={() => toggleChart(elem.title)}
                                    className={styles.toggleButton}
                                    aria-label={`Toggle ${elem.title} chart`}
                                >
                                    {showChart.includes(elem.title) ? 'Hide Chart' : 'Show Chart'}
                                </button>
                            ) : null}
                        </h3>
                        {displayChart(elem.title)}

                        <div className={styles.statsContainer}>
                            {elem.values.map((value: any, i2: number) => {
                                const label = Object.keys(value)[0]
                                if (label === CLIENTS_WITH_CLAIMS_LABEL && showFundableSwap) {
                                    // Keep the tile label but show the fundable figure instead of
                                    // the legacy quantum-based count, so it matches the card.
                                    const fundableStat = fundableLoading
                                        ? '…'
                                        : fundableError
                                        ? '—'
                                        : formatFundable(fundableData?.fundableClients)
                                    return <StatTile key={i2} title={label} stat={fundableStat} />
                                }
                                return <StatTile key={i2} title={label} stat={`${Object.values(value)[0]}`} />
                            })}
                        </div>
                    </div>
                </div>
            )
        })
    }

    return (
        <div className={styles.statsView}>
            <div>{showStats ? <>{displayStats()}</> : null}</div>
        </div>
    )
}

export interface statsProps {
    stats: any
}
